import React, { useEffect, useRef } from 'react';

const RenderPdfFromBase64 = ({ base64 }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    // Make sure PDF.js is loaded
    const loadPdfJs = async () => {
      // Add PDF.js script if not already loaded
      if (!window.pdfjsLib) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.async = true;
        await new Promise((resolve) => {
          script.onload = resolve;
          document.head.appendChild(script);
        });
      }

      // Initialize the worker
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

      renderPdf();
    };

    const renderPdf = async () => {
      if (!base64 || !containerRef.current || !window.pdfjsLib) return;

      // Clear the container
      containerRef.current.innerHTML = '';

      try {
        // Convert base64 to binary data
        const raw = atob(base64);
        const uint8Array = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) {
          uint8Array[i] = raw.charCodeAt(i);
        }

        // Load the PDF document
        const loadingTask = window.pdfjsLib.getDocument({ data: uint8Array });
        const pdf = await loadingTask.promise;

        // Loop through each page
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);

          // Create a canvas for each page
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          // Set scale for rendering
          const viewport = page.getViewport({ scale: 1.5 });
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          canvas.style.marginBottom = '10px';

          // Render the page
          const renderContext = {
            canvasContext: context,
            viewport: viewport
          };

          await page.render(renderContext).promise;
          containerRef.current.appendChild(canvas);
        }
      } catch (error) {
        console.error('Error rendering PDF:', error);
        // Fallback to showing as image if PDF rendering fails
        showAsFallbackImage();
      }
    };

    const showAsFallbackImage = () => {
      if (!base64) return;

      // Try to display as an image (in case it's not actually a PDF)
      containerRef.current.innerHTML = '';
      const img = document.createElement('img');
      img.src = `data:image/png;base64,${base64}`;
      img.style.maxWidth = '100%';
      img.onerror = () => {
        containerRef.current.innerHTML = 'Unable to display this content';
      };
      containerRef.current.appendChild(img);
    };

    loadPdfJs();

    // Cleanup function
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [base64]);

  return <div ref={containerRef} style={{ width: '100%' }} />;
};

export default RenderPdfFromBase64;